import { SlidersHorizontal } from "@phosphor-icons/react";
import { useMemo, useState } from "react";

import Alert from "common/components/ui/Alert";
import { Button } from "common/components/ui/Button";
import { DialogDescription, DialogTitle } from "common/components/ui/Dialog";
import { DrawerHeader } from "common/components/ui/Drawer";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { WorkspaceAccount } from "common/types";

import useAccountLimitsMutations from "../../datahooks/useAccountLimitsMutations";
import { startAccountsBulkAction } from "../../utils";
import AccountLimits from "../AccountLimits";

import type { AccountLimits as AccountLimitsType } from "../../types";

export default function ManageInteractions({
  selectedAccounts,
  onBack,
}: {
  selectedAccounts: WorkspaceAccount[];
  onBack: () => void;
}) {
  const { permissions } = useSelectedWorkspaceContext();
  const isDesktop = useTwBreakpoint("lg");
  const [accountLimitsState, setAccountLimitsState] =
    useState<AccountLimitsType>({
      connect: 100,
      inmail: 100,
      message_request: 100,
      warmup: { enabled: false },
    });

  const { setAccountLimits } = useAccountLimitsMutations();

  const userAccounts = useMemo(
    () => selectedAccounts.filter(({ type }) => type === "user"),
    [selectedAccounts],
  );

  const rentedAccountsCount = selectedAccounts.length - userAccounts.length;

  function updateInteractions() {
    startAccountsBulkAction(
      (accountId, abortSignal) =>
        setAccountLimits({
          accountId,
          limits: accountLimitsState,
          abortSignal,
        }),
      userAccounts,
      (finishedItemsCount) =>
        finishedItemsCount === userAccounts.length
          ? "Interaction limits updated"
          : `Changing ${finishedItemsCount}/${userAccounts.length} interaction limits`,
    );
    onBack();
  }

  const isViewOnly = !permissions.includes("accounts_full");

  const title = "Manage interactions";
  const description = `Change weekly interaction limits for ${userAccounts.length === 1 ? userAccounts[0].full_name : `${userAccounts.length} accounts`}?`;

  return (
    <>
      {isDesktop ? (
        <>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="mb-4">{description}</DialogDescription>
        </>
      ) : (
        <DrawerHeader title={title} subtitle={description} onBack={onBack} />
      )}

      <div className="flex flex-col overflow-y-auto px-2 py-4 scrollbar-thin gap-y-2">
        <div className="px-2 py-2.5 flex text-button-sm gap-x-1.5">
          <SlidersHorizontal size={20} weight="fill" />
          Interaction limits / week
        </div>
        <AccountLimits
          accountLimits={accountLimitsState}
          setAccountLimits={setAccountLimitsState}
          isLoading={false}
          isViewOnly={isViewOnly}
        />
        {rentedAccountsCount > 0 && (
          <Alert variant="warning">
            Any changes to interactions settings will not affect $
            {rentedAccountsCount} selected Outreach Avatar(s)
          </Alert>
        )}
      </div>

      <div className="justify-between mt-4 flex">
        <Button
          variant="secondary-black"
          size={isDesktop ? "md" : "lg"}
          onClick={onBack}
        >
          Back
        </Button>
        <Button size={isDesktop ? "md" : "lg"} onClick={updateInteractions}>
          Save
        </Button>
      </div>
    </>
  );
}
